<template>
  <div class="adv-button-view-buy-picker quick-v-tocart">
    <slot name="content"></slot>
    <AdvAddToCartButtonBase
      v-if="!showQuantityPicker || (hasAttributes && isListing)"
      key="add-to-cart-button"
      :disabled="getDisabled"
      :button-text="buttonText"
      @add-to-cart="btnBuyClickedNoSpinner"
    />
    <AdvQuantityPickerBase
      v-else
      key="quantity-picker"
      :disabled="getDisabled"
      :quantity="cartQuantity"
      :product-id="productId"
      :product-code-id="productCodeId"
      @quantity-changed="updateProductQuantity"
      @remove-from-cart="removeProductFromCart"
    />
  </div>
</template>

<script>
import productMixin from './mixins/productMixins'
import onlineMixin from './mixins/onlineMixin'
import cartButtonMixin from './mixins/cartButtonMixin'
import { mapActions, mapGetters } from 'vuex'
import AdvAddToCartButtonBase from './AdvAddToCartButtonBase.vue'
import AdvQuantityPickerBase from './AdvQuantityPickerBase.vue'
import store from '../../vue/store'
import { some } from 'lodash'

export default {
  name: 'AdvButtonViewBuyPicker',
  mixins: [productMixin, onlineMixin, cartButtonMixin],
  components: {
    AdvAddToCartButtonBase,
    AdvQuantityPickerBase
  },
  props: {
    productId: {
      type: Number,
      default: null
    },
    productCodeId: {
      type: Number,
      default: null
    },
    showQuickView: {
      type: Boolean,
      default: false
    },
    isListing: {
      type: Boolean,
      default: false
    },
    isVariationGroupMaster: {
      type: Boolean,
      default: false
    },
    unavailableProduct: {
      type: Boolean,
      default: false
    },
    recommendationType: {
      type: String,
      default: null
    },
    recommendationId: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      quickViewVisible: false,
      progress: false,
      showQuantityPicker: false
    }
  },
  mounted () {
    if (this.cartQuantity > 0) {
      this.showQuantityPicker = true
    }
  },
  watch: {
    cartQuantity (val) {
      this.showQuantityPicker = val > 0
    }
  },
  computed: {
    ...mapGetters([
      'cartProductCodeQuantity',
      'productCardModal'
    ]),
    cartProductContents () {
      return store.getters.cartContents
    },
    cartProduct () {
      return this.cartProductContents.find(item => item.productCodeId === this.productCodeId)
    },
    cartQuantity () {
      return this.cartProductCodeQuantity(this.productCodeId, 0)
    },
    stock () {
      return this.productData.productStock
    },
    hasAttributes () {
      return some(this.productData.productCodes, (e) => {
        return e.productCodeAttributes.length > 0
      })
    },
    getDisabled () {
      return this.disabled || this.unavailableProduct
    }
  },
  methods: {
    ...mapActions([
      'addToCartClicked',
      'openProductCardModal',
      'closeProductCardModal'
    ]),
    toggleQuickView () {
      if (store.state.productCardModal.open) {
        store.dispatch('closeProductCardModal')
      } else {
        store.dispatch('openProductCardModal', this.productId)
      }
    },
    updateProductQuantity (quantity) {
      this.progress = true
      const cartPayload = {
        productCodeId: this.cartProduct.productCodeId,
        quantity,
        recommendationId: this.recommendationId,
        recommendationType: this.recommendationType
      }
      if ('cartProductCustomizations' in this.cartProduct.options) {
        cartPayload.cartProductCustomizations = this.cartProduct.options.cartProductCustomizations
      }
      store.dispatch('addToCartClicked', cartPayload).then(() => {
        this.progress = false
      })
    },
    removeProductFromCart () {
      this.showQuantityPicker = false
      const cartPayload = {
        productCodeId: this.cartProduct.productCodeId,
        quantity: 0
      }
      if ('cartProductCustomizations' in this.cartProduct.options) {
        cartPayload.cartProductCustomizations = this.cartProduct.options.cartProductCustomizations
      }
      store.dispatch('addToCartClicked', cartPayload)
    },
    btnBuyClickedNoSpinner () {
      if (this.hasAttributes && this.isListing) {
        this.toggleQuickView()
        return
      }

      if (this.stock <= 0 && (!this.isListing && !this.isVariationGroupMaster)) {
        this.btnBuyClickedNoStock()
        return
      }

      if (this.isListing && this.isVariationGroupMaster) {
        this.btnBuyClickedForVariationMaster()
        return
      }

      this.progress = true
      this.addToCartClicked({
        productCodeId: this.productCodeId,
        quantity: 1,
        recommendationId: this.recommendationId,
        recommendationType: this.recommendationType
      })
        .then(() => {
          this.progress = false
        })
    },
    btnBuyClickedNoStock () {
      // for clients that need click event without stock on button
    },
    /**
     * This is a special case for variation group master products.
     * If the product is a variation group master,
     * and we are in a listing page, we need to redirect to the product's page.
     */
    btnBuyClickedForVariationMaster () {
      // old comment below fwiw:
      // for variations: if isListing && isVariationGroupMaster emits btn-click-no-stock so here we are
      if (this.isListing && this.isVariationGroupMaster) {
        window.location.href = this.productData.productUrl
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.adv-button-view-buy-picker {
  width: 100%;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  position: relative;
  height:fit-content;
  overflow: hidden;
}
</style>
